import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBrain, faListCheck, faNoteSticky, faTerminal, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import Sidebar from '../components/layout/Sidebar';
import PageHeader from '../components/PageHeader';
import AiJobChips from '../components/ai/AiJobChips';
import AiQueueCards from '../components/ai/AiQueueCards';
import QueueBreakerToggle from '../components/ai/QueueBreakerToggle';
import AutoApproveToggles from '../components/ai/AutoApproveToggles';
import useQueueBreaker from '../components/ai/useQueueBreaker';
import PromptsView from '../components/agents/PromptsView';
import NoteTemplatesView from '../components/agents/NoteTemplatesView';
import TaskTypesView from '../components/agents/TaskTypesView';
import { useUrlParam, useUrlPanelSync } from '../hooks/useUrlParam';
import { AgentsPanel } from '../features/settings';
import { Badge, Card, PulseDot } from '../components/ui';
import { useUser } from '../context/UserContext';
import { fetchJobById, fetchQueueJobs, IN_PROGRESS_STATUSES, type AiJob } from '../lib/aiJobs';
import { AI_JOB_STATUS_LABEL } from '../lib/aiJobLabels';
import { uniqueChannelName } from '../lib/realtime';
import { supabase } from '../lib/supabase';

interface AgentRuntime {
  id: string;
  slug: string;
  name: string;
  kind: 'openclaw' | 'hermes';
  is_active: boolean;
  last_seen_at: string | null;
  last_claimed_at: string | null;
}

interface Section {
  path: string;
  label: string;
  icon: IconDefinition;
}

const SECTIONS: Section[] = [
  { path: '/agents', label: 'Queues', icon: faListCheck },
  { path: '/agents/brains', label: 'On-disk brains', icon: faBrain },
  { path: '/agents/task-types', label: 'Task types', icon: faWandMagicSparkles },
  { path: '/agents/prompts', label: 'Prompts', icon: faTerminal },
  { path: '/agents/note-templates', label: 'Note templates', icon: faNoteSticky },
];

const formatWhen = (value: string | null) => {
  if (!value) return 'never';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'never';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Presence follows the breaker and the claims, not heartbeat guesswork:
 *   breaker off + claimed job    → Finishing up (drains the current job)
 *   breaker off                  → Offline
 *   breaker on + claimed job     → that job's status ("what they're doing")
 *   breaker on + work waiting:
 *     no claim yet this session  → Starting up (only before the first task)
 *     already worked this session→ Idle (between tasks)
 *   breaker on, nothing to take  → Waiting
 * "This session" starts when the breaker last flipped (queue_breaker.updated_at);
 * ai_jobs stamps agent_runtimes.last_claimed_at on every claim.
 */
function runtimePresence(
  runtime: AgentRuntime,
  breaker: { on: boolean; changedAt: string | null },
  hasQueuedWork: boolean,
  activeJobs: AiJob[]
) {
  if (!runtime.is_active) {
    return { label: 'Inactive', tone: 'idle' as const, pulse: false };
  }
  const doing = activeJobs.find((job) => {
    const claimant = (job.claimed_by || '').trim().toLowerCase();
    return claimant === runtime.name.toLowerCase() || claimant === runtime.slug.toLowerCase();
  });
  if (!breaker.on) {
    if (doing) {
      return { label: 'Finishing up', tone: 'warn' as const, pulse: true };
    }
    return { label: 'Offline', tone: 'idle' as const, pulse: false };
  }
  if (doing) {
    return { label: AI_JOB_STATUS_LABEL[doing.status], tone: 'info' as const, pulse: true };
  }
  if (hasQueuedWork) {
    const sessionStart = breaker.changedAt ? Date.parse(breaker.changedAt) : NaN;
    const lastClaim = runtime.last_claimed_at ? Date.parse(runtime.last_claimed_at) : NaN;
    const workedThisSession =
      Number.isFinite(lastClaim) && (!Number.isFinite(sessionStart) || lastClaim >= sessionStart);
    if (!workedThisSession) {
      return { label: 'Starting up', tone: 'warn' as const, pulse: true };
    }
    return { label: 'Idle', tone: 'up' as const, pulse: false };
  }
  return { label: 'Waiting', tone: 'idle' as const, pulse: false };
}

function presenceBadgeTone(tone: 'up' | 'warn' | 'info' | 'down' | 'idle') {
  if (tone === 'info') return 'info' as const;
  if (tone === 'up') return 'up' as const;
  if (tone === 'warn') return 'warn' as const;
  return 'neutral' as const;
}

function SectionChip({ section, active }: { section: Section; active: boolean }) {
  return (
    <Link
      to={section.path}
      className="pressable min-h-[40px] md:min-h-0"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 12px',
        borderRadius: 'var(--radius-sm)',
        border: '1px solid ' + (active ? 'var(--border-strong)' : 'transparent'),
        background: active ? 'var(--surface-active)' : 'transparent',
        color: active ? 'var(--text-body)' : 'var(--text-muted)',
        fontFamily: 'var(--font-ui)',
        fontSize: 'var(--text-sm)',
        fontWeight: (active
          ? 'var(--weight-medium)'
          : 'var(--weight-regular)') as React.CSSProperties['fontWeight'],
        whiteSpace: 'nowrap',
        textDecoration: 'none',
        transition:
          'background var(--dur-base) var(--ease-out), border-color var(--dur-base) var(--ease-out), color var(--dur-base) var(--ease-out)',
      }}
    >
      <FontAwesomeIcon icon={section.icon} style={{ fontSize: '12px', width: '14px' }} />
      {section.label}
    </Link>
  );
}

/** Slim presence strip above the queues. */
function RuntimeStrip({
  runtimes,
  queuedCount,
  activeJobs,
  breaker,
}: {
  runtimes: AgentRuntime[];
  queuedCount: number;
  activeJobs: AiJob[];
  breaker: { on: boolean; changedAt: string | null };
}) {
  const hasQueuedWork = queuedCount > 0;

  return (
    <Card padding="12px 18px" style={{ marginBottom: 'var(--space-5)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px 16px', flexWrap: 'wrap' }}>
        {runtimes.length === 0 ? (
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
            No runtimes yet — waiting for a worker to publish.
          </span>
        ) : (
          runtimes.map((runtime, index) => {
            const presence = runtimePresence(runtime, breaker, hasQueuedWork, activeJobs);
            return (
              <Fragment key={runtime.id}>
                {index > 0 ? (
                  <span
                    aria-hidden
                    className="ck-strip-divider"
                    style={{ width: '1px', alignSelf: 'stretch', background: 'var(--border-hairline)' }}
                  />
                ) : null}
                {/* Phones stack the runtimes, so they read left-aligned like a list; desktop centres each column. */}
                <div className="items-start md:items-center" style={{ flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', whiteSpace: 'nowrap', flexWrap: 'wrap' }}>
                    <PulseDot tone={presence.tone} pulse={presence.pulse} title={presence.label} />
                    <span
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                        color: 'var(--text-body)',
                      }}
                    >
                      {runtime.name}
                    </span>
                    <span className="t-eyebrow" style={{ color: 'var(--text-faint)' }}>
                      {runtime.kind}
                    </span>
                    <span className="ck-badge-flip">
                      <span key={presence.label} className="ck-badge-flip-inner">
                        <Badge tone={presenceBadgeTone(presence.tone)}>{presence.label}</Badge>
                      </span>
                    </span>
                  </div>
                  <span className="pl-[18px] md:pl-0" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                    seen {formatWhen(runtime.last_seen_at)}
                  </span>
                </div>
              </Fragment>
            );
          })
        )}
      </div>
    </Card>
  );
}

function QueuesView() {
  const [runtimes, setRuntimes] = useState<AgentRuntime[]>([]);
  const [queuedCount, setQueuedCount] = useState(0);
  const [activeJobs, setActiveJobs] = useState<AiJob[]>([]);
  const [openJob, setOpenJob] = useState<AiJob | null>(null);
  // The open job is ?job=<id>: opening one sets it, closing clears it, so refresh and back keep your place.
  const [jobParam, setJobParam] = useUrlParam('job');
  useUrlPanelSync(
    jobParam,
    openJob?.id ?? null,
    true,
    (id) => {
      void fetchJobById(id).then((job) => {
        if (job) setOpenJob(job);
      });
    },
    () => setOpenJob(null),
  );
  const breakerState = useQueueBreaker();

  const loadStatus = useCallback(async () => {
    try {
      const [{ data: runtimeRows, error: runtimeError }, watch, active] = await Promise.all([
        supabase.from('agent_runtimes').select('id, slug, name, kind, is_active, last_seen_at, last_claimed_at').order('name'),
        fetchQueueJobs({ limit: 50 }),
        fetchQueueJobs({ status: IN_PROGRESS_STATUSES, limit: 50 }),
      ]);
      if (runtimeError) throw runtimeError;
      setRuntimes((runtimeRows || []) as AgentRuntime[]);
      setQueuedCount(watch.length);
      setActiveJobs(active);
    } catch (err) {
      console.error('Error loading agent status:', err);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    const channel = supabase
      .channel(uniqueChannelName('agents_status'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'agent_runtimes' }, loadStatus)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ai_jobs' }, loadStatus)
      .subscribe();
    const poll = window.setInterval(loadStatus, 15000);
    return () => {
      supabase.removeChannel(channel);
      window.clearInterval(poll);
    };
  }, [loadStatus]);

  return (
    <>
      <RuntimeStrip
        runtimes={runtimes}
        queuedCount={queuedCount}
        activeJobs={activeJobs}
        breaker={{ on: breakerState.isOn === true, changedAt: breakerState.changedAt }}
      />

      <div
        className="grid grid-cols-1 xl:grid-cols-2"
        style={{ gap: 'var(--space-5)', alignItems: 'start' }}
      >
        <AiQueueCards onOpenJob={(job) => setJobParam(job.id)} />
      </div>

      {openJob ? (
        <AiJobChips
          key={openJob.id}
          job={openJob}
          panelOnly
          initialOpenJobId={openJob.id}
          onClosePanel={() => setJobParam(null)}
        />
      ) : null}
    </>
  );
}

export default function Agents() {
  const { user } = useUser();
  const location = useLocation();

  const isAdmin = user?.role?.trim?.().toLowerCase?.() === 'admin';
  if (!isAdmin) {
    return <Navigate to="/settings" replace />;
  }

  return (
    <div className="min-h-screen">
      <Sidebar />
      <main className="px-4 md:px-8 pb-24 md:pb-8 md:ml-[72px]">
        <PageHeader title="Agents" />

        <div
          className="flex flex-wrap items-center justify-between"
          style={{
            gap: '10px',
            padding: '0 0 10px',
            borderBottom: '1px solid var(--border-hairline)',
            marginTop: 'calc(var(--space-4) * -1)',
            marginBottom: 'var(--space-6)',
          }}
        >
          {/* Phones: a two-column grid of finger-sized chips instead of a ragged wrap. */}
          <div className="grid w-full grid-cols-2 md:flex md:w-auto md:flex-wrap md:items-center" style={{ gap: '6px' }}>
            {SECTIONS.map((section) => (
              <SectionChip
                key={section.path}
                section={section}
                active={section.path === '/agents' ? location.pathname === '/agents' || location.pathname === '/agents/' : location.pathname.startsWith(section.path)}
              />
            ))}
          </div>
          <div className="flex flex-wrap items-center" style={{ gap: '12px 20px' }}>
            <AutoApproveToggles />
            {/* The divider only reads as one when the two sit side by side. */}
            <span aria-hidden className="hidden md:block" style={{ width: '1px', alignSelf: 'stretch', background: 'var(--border-hairline)' }} />
            <QueueBreakerToggle />
          </div>
        </div>

        <div className="ck-stagger">
          <Routes>
            <Route index element={<QueuesView />} />
            <Route path="brains" element={<AgentsPanel />} />
            <Route path="task-types" element={<TaskTypesView />} />
            <Route path="task-types/:typeId" element={<TaskTypesView />} />
            <Route path="prompts" element={<PromptsView />} />
            <Route path="prompts/:promptId" element={<PromptsView />} />
            <Route path="note-templates" element={<NoteTemplatesView />} />
            <Route path="design" element={<Navigate to="/agents/task-types/general" replace />} />
            <Route path="*" element={<Navigate to="." replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
